import React from 'react';
import { InertiaFormProps } from '@inertiajs/react/types/useForm';
import { FileViewerProps } from './FileViewer';
import { useForm } from '@inertiajs/react';
import ButtonSimple from '@/Components/ButtonSimple';
import InputError from '@/Components/InputError';
import useRoute from '@/Hooks/useRoute';
import IconSave from '@/svgs/check.svg?react';
import IconCopy from '@/svgs/file-copy.svg?react';
import IconShare from '@/svgs/ios-share.svg?react';
import useTypedPage from '@/Hooks/useTypedPage';
import FileShare from '@/Pages/Files/Partials/FileShare';
import CopyLinkButton from '@/Components/CopyLinkButton';
import { cn } from '@/utils';

type FormProps = { name: string; description: string };

export default function SnippetMeta({
  file,
  editting = false,
  onMetaSave,
}: FileViewerProps) {
  const form = useForm<FormProps>({
    name: file.name,
    description: file.description ?? '',
  });

  if (editting) {
    return <Edit file={file} form={form} onSuccess={onMetaSave} />;
  } else if (form.data.name || form.data.description) {
    return <Show form={form} />;
  }
}

function Edit({
  file,
  form,
  onSuccess,
}: {
  file: App.Data.FileData;
  form: InertiaFormProps<FormProps>;
  onSuccess?: () => void;
}) {
  const route = useRoute();

  async function handleUpdateFile(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    form.put(route('files.update', file.id), {
      errorBag: 'updateFile',
      preserveScroll: true,
      onSuccess,
    });
  }

  return (
    <form
      id="editMetadata"
      className="mb-6 space-y-4 border-b border-brand-50 pb-6 dark:border-brand-dark-50"
      onSubmit={handleUpdateFile}
    >
      <input
        type="text"
        name="name"
        className="w-full border-none bg-transparent p-0 text-xl font-semibold tracking-tight dark:text-brand-light-100"
        placeholder="Enter a snippet name"
        value={form.data.name}
        onChange={e => form.setData('name', e.currentTarget.value)}
      />
      <InputError message={form.errors.name} className="mt-2" />
      <textarea
        placeholder="Enter a snippet description"
        name="description"
        value={form.data.description}
        onChange={e => form.setData('description', e.currentTarget.value)}
        className="w-full rounded-lg border-none bg-brand-50 p-4 dark:bg-brand-dark-75 dark:text-brand-light-25"
      />
      <InputError message={form.errors.description} className="mt-2" />
      <ButtonSimple
        type="submit"
        className="flex gap-2 px-4 dark:bg-brand-dark-75"
      >
        Save
        <IconSave className="h-4 w-4" />
      </ButtonSimple>
    </form>
  );
}

function Show({ form }: { form: InertiaFormProps<FormProps> }) {
  const page = useTypedPage();
  return (
    <div className="mb-6 space-y-4 border-b pb-6 dark:border-brand-dark-50">
      {form.data.name && (
        <div className={cn(
          "flex justify-between items-stretch flex-col",
          "md:flex-row md:items-center"
        )}>
          <h1 className={cn(
            "text-xl text-center font-semibold tracking-tight dark:text-brand-light-100 mb-4",
            "md:mb-0"
          )}>
            {form.data.name}
          </h1>
          <div className="flex items-center">
            <input
              value={page.props.short_url}
              className={cn(
                "w-full dark:bg-brand-dark-75 text-lg border-brand-dark p-3 pl-2 rounded-l-md active:bg-brand-dark-75",
                "md:w-40 md:text-sm md:p-1"
              )}
              readOnly={true}
            />
            <div className={cn(
              "self-stretch flex items-center p-3 px-4 bg-brand-dark text-brand-dark-75 cursor-pointer hover:dark:bg-brand-dark-50 hover:dark:text-brand-light-100",
              "md:p-1 md:px-2"
            )}>
              <CopyLinkButton
                url={page.props.short_url}
                className="hover:dark:text-brand-light-100"
              >
                <IconCopy className="h-5 w-5"/>
              </CopyLinkButton>
            </div>
            <div className={cn(
              "self-stretch flex items-center p-3 px-4 bg-brand-dark text-brand-dark-75 rounded-r-md cursor-pointer hover:dark:bg-brand-dark-50 hover:dark:text-brand-light-100",
              "md:p-1 md:px-2"
            )}>
              <FileShare file={page.props.file}>
                <div>
                  <IconShare className="h-5 w-5"/>
                </div>
              </FileShare>
            </div>
          </div>
        </div>
      )}
      {form.data.description && (
        <div className="rounded-lg p-4 dark:bg-brand-dark-75 dark:text-brand-light-25">
          <p>{form.data.description}</p>
        </div>
      )}
    </div>
  );
}
